import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAcList, useAcMutations, type AcAlert } from "../api/accessControlApi";
import {
  CAMERA_ICON,
  ALERT_STATUS_TONE,
  ConsolePage,
  DetailRow,
  Drawer,
  FilterSelect,
  KpiRow,
  KpiTile,
  LoadState,
  Panel,
  SEVERITY_TONE,
  SearchBox,
  StatusChip,
  ToolButton,
  formatDateTime,
  relativeTime,
  toneOf,
  useTicker,
} from "../components/console/Console";
import { NUVEX_ACCESS_BASE } from "../paths";

// ===========================================================================
// Seguridad y vigilancia → Alertas: centro de eventos de alarma (iVMS-4200
// «Centro de eventos»): filtros, tabla con la captura de la cámara y panel
// lateral para atender y resolver con notas.
// ===========================================================================

const SEVERITIES = ["critical", "high", "medium", "low"] as const;
const STATUSES = ["open", "acknowledged", "resolved"] as const;

export const ALERT_ICON: Record<string, string> = {
  intrusion: "ri-spy-line",
  line_crossing: "ri-route-line",
  loitering: "ri-user-location-line",
  tamper: "ri-eye-off-line",
  video_loss: "ri-video-off-line",
  door_forced: "ri-door-open-line",
  door_held_open: "ri-door-lock-box-line",
  denied_access: "ri-shield-cross-line",
  blacklist_plate: "ri-car-washing-line",
  panic: "ri-alarm-line",
  fire: "ri-fire-line",
};

const thumbOf = (url?: string | null) => (url ? url.replace("/sandbox/camaras/", "/sandbox/camaras/thumbs/") : null);

export default function AlertsConsole() {
  const { t, i18n } = useTranslation("accessConsole");
  const navigate = useNavigate();
  const now = useTicker();
  const alertsQ = useAcList("alerts", undefined, { refetchInterval: 30_000 });
  const sitesQ = useAcList("sites");
  const { update } = useAcMutations("alerts");

  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("");
  const [status, setStatus] = useState("active");
  const [site, setSite] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  const alerts = useMemo(() => alertsQ.data ?? [], [alertsQ.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return alerts.filter(
      (a) =>
        (!severity || a.severity === severity) &&
        (!status || (status === "active" ? a.status !== "resolved" : a.status === status)) &&
        (!site || a.site_id === site) &&
        (!q || [a.title, a.description, a.camera_name, a.site_name].some((x) => (x ?? "").toLowerCase().includes(q))),
    );
  }, [alerts, search, severity, status, site]);

  const open = alerts.filter((a) => a.status === "open");
  const current = alerts.find((a) => a.id === openId) ?? null;

  const setAlertStatus = async (a: AcAlert, next: AcAlert["status"]) => {
    try {
      await update.mutateAsync({ id: a.id, body: { status: next, ...(next === "resolved" && notes.trim() ? { resolution_notes: notes.trim() } : {}) } });
      toast.success(t(`alerts.toast.${next}`));
      setNotes("");
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  return (
    <ConsolePage icon="ri-alarm-warning-line" title={t("alerts.title")} subtitle={t("alerts.subtitle")}>
      <KpiRow>
        <KpiTile icon="ri-alarm-warning-line" tone="error" label={t("kpi.openAlerts")} value={open.length} />
        {SEVERITIES.map((s) => (
          <KpiTile key={s} icon="ri-error-warning-line" tone={toneOf(SEVERITY_TONE, s)} label={t(`severity.${s}`)} value={alerts.filter((a) => a.severity === s && a.status !== "resolved").length} hint={t("alerts.unresolved")} />
        ))}
        <KpiTile icon="ri-checkbox-circle-line" tone="success" label={t("alertStatus.resolved")} value={alerts.filter((a) => a.status === "resolved").length} />
      </KpiRow>

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={setSearch} placeholder={t("alerts.searchPlaceholder")} />
        <FilterSelect label={t("fields.severity")} value={severity} onChange={setSeverity} options={[{ value: "", label: t("alerts.allSeverities") }, ...SEVERITIES.map((s) => ({ value: s, label: t(`severity.${s}`) }))]} />
        <FilterSelect label={t("fields.status")} value={status} onChange={setStatus} options={[{ value: "active", label: t("alerts.unresolved") }, { value: "", label: t("common.all") }, ...STATUSES.map((s) => ({ value: s, label: t(`alertStatus.${s}`) }))]} />
        <FilterSelect label={t("fields.site")} value={site} onChange={setSite} options={[{ value: "", label: t("common.allSites") }, ...(sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
        <span className="ml-auto text-xs text-content-muted">{t("common.resultsN", { interpolation: { escapeValue: false }, count: rows.length })}</span>
      </div>

      <Panel bodyClassName="overflow-x-auto">
        <LoadState loading={alertsQ.isLoading} error={alertsQ.error} empty={!rows.length} emptyText={t("alerts.empty")} onRetry={() => alertsQ.refetch()}>
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("fields.capture")}</th>
                <th className="px-3 py-2">{t("fields.alert")}</th>
                <th className="px-3 py-2">{t("fields.source")}</th>
                <th className="px-3 py-2">{t("fields.severity")}</th>
                <th className="px-3 py-2">{t("fields.status")}</th>
                <th className="px-3 py-2">{t("fields.when")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((a) => (
                <tr key={a.id} onClick={() => { setOpenId(a.id); setNotes(a.resolution_notes ?? ""); }} className="cursor-pointer hover:bg-subtle">
                  <td className="px-3 py-2">
                    {thumbOf(a.snapshot_url) ? <img src={thumbOf(a.snapshot_url) as string} alt="" className="h-11 w-20 rounded object-cover" loading="lazy" /> : <span className="text-content-faint">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 font-medium text-content">
                      <i className={`${ALERT_ICON[a.type] ?? "ri-alarm-warning-line"} text-content-muted`} />
                      {a.title}
                    </div>
                    <div className="text-xs text-content-secondary">{t(`alertType.${a.type}`)}</div>
                  </td>
                  <td className="px-3 py-2 text-content-secondary">
                    <div className="text-content">{a.camera_name ?? "—"}</div>
                    <div className="text-xs">{a.site_name}</div>
                  </td>
                  <td className="px-3 py-2"><StatusChip tone={toneOf(SEVERITY_TONE, a.severity)} pulse={a.severity === "critical" && a.status === "open"}>{t(`severity.${a.severity}`)}</StatusChip></td>
                  <td className="px-3 py-2"><StatusChip tone={toneOf(ALERT_STATUS_TONE, a.status)}>{t(`alertStatus.${a.status}`)}</StatusChip></td>
                  <td className="whitespace-nowrap px-3 py-2 text-content-secondary">
                    <div>{relativeTime(a.occurred_at, i18n.language, now)}</div>
                    <div className="text-xs text-content-muted">{formatDateTime(a.occurred_at, i18n.language)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </LoadState>
      </Panel>

      <Drawer
        open={Boolean(current)}
        title={current?.title ?? ""}
        subtitle={current ? `${t(`alertType.${current.type}`)} · ${current.site_name ?? ""}` : undefined}
        onClose={() => setOpenId(null)}
        footer={
          current && (
            <>
              <ToolButton icon={CAMERA_ICON} onClick={() => navigate(`${NUVEX_ACCESS_BASE}/seguridad-y-vigilancia/camaras`)}>{t("alerts.viewCamera")}</ToolButton>
              {current.status === "open" && <ToolButton icon="ri-eye-line" onClick={() => setAlertStatus(current, "acknowledged")} disabled={update.isPending}>{t("alerts.acknowledge")}</ToolButton>}
              {current.status !== "resolved" && <ToolButton icon="ri-check-double-line" variant="primary" onClick={() => setAlertStatus(current, "resolved")} disabled={update.isPending}>{t("alerts.resolve")}</ToolButton>}
            </>
          )
        }
      >
        {current && (
          <div className="space-y-3">
            {current.snapshot_url && (
              <div className="relative overflow-hidden rounded-lg bg-black">
                <img src={current.snapshot_url} alt={current.camera_name ?? ""} className="aspect-video w-full object-cover" />
                <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[11px] text-white">{formatDateTime(current.occurred_at, i18n.language)}</span>
                <span className="absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[11px] text-white">{current.camera_name}</span>
              </div>
            )}
            <div className="flex gap-1.5">
              <StatusChip tone={toneOf(SEVERITY_TONE, current.severity)}>{t(`severity.${current.severity}`)}</StatusChip>
              <StatusChip tone={toneOf(ALERT_STATUS_TONE, current.status)}>{t(`alertStatus.${current.status}`)}</StatusChip>
            </div>
            {current.description && <p className="text-sm text-content-secondary">{current.description}</p>}
            <div>
              <DetailRow label={t("fields.when")}>{formatDateTime(current.occurred_at, i18n.language)}</DetailRow>
              <DetailRow label={t("fields.camera")}>{current.camera_name ?? "—"}</DetailRow>
              <DetailRow label={t("fields.site")}>{current.site_name ?? "—"}</DetailRow>
              <DetailRow label={t("alerts.acknowledgedAt")}>{current.acknowledged_at ? `${formatDateTime(current.acknowledged_at, i18n.language)}${current.acknowledged_by ? ` · ${current.acknowledged_by}` : ""}` : "—"}</DetailRow>
              <DetailRow label={t("alerts.resolvedAt")}>{formatDateTime(current.resolved_at, i18n.language)}</DetailRow>
              <DetailRow label={t("alerts.assignedTo")}>{current.assigned_to ?? "—"}</DetailRow>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-content-secondary">{t("alerts.notes")}</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={current.status === "resolved"}
                rows={3}
                className="w-full rounded-lg border border-line bg-app px-3 py-2 text-sm text-content outline-none focus:border-primary-500"
                placeholder={t("alerts.notesPlaceholder")}
              />
            </label>
          </div>
        )}
      </Drawer>
    </ConsolePage>
  );
}
